/**
 * Safely adds an event listener to an element, checking if it exists first
 * @param selector CSS selector for the element
 * @param eventType Event type to listen for
 * @param callback Function to execute when event triggers
 * @param options Optional addEventListener options
 */
export function safeAddEventListener(
  selector: string,
  eventType: string,
  callback: EventListenerOrEventListenerObject,
  options?: boolean | AddEventListenerOptions
): void {
  // Function to add the event listener when the element exists
  const addListener = () => {
    const element = document.querySelector(selector);
    if (element) {
      element.addEventListener(eventType, callback, options);
      console.log(`Event listener added to ${selector}`);
    }
  };

  // If document is already loaded, try to add the listener immediately
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    addListener();
  } else {
    // Otherwise wait for DOM to be fully loaded
    document.addEventListener('DOMContentLoaded', addListener);
  }

  // Also add a mutation observer to catch elements that might be added dynamically
  // after initial page load
  const observer = new MutationObserver((mutations) => {
    if (document.querySelector(selector)) {
      addListener();
      observer.disconnect(); // Stop observing once we've found the element
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
}

/**
 * Creates a script that runs only when a specific DOM element exists
 * @param selector CSS selector for required element
 * @param scriptCallback Function to run when element exists
 */
export function runScriptWhenElementExists(
  selector: string,
  scriptCallback: (element: Element) => void
): void {
  // Check if element already exists
  const element = document.querySelector(selector);
  if (element) {
    scriptCallback(element);
    return;
  }

  // Set up observer to watch for the element
  const observer = new MutationObserver((mutations, obs) => {
    const element = document.querySelector(selector);
    if (element) {
      scriptCallback(element);
      obs.disconnect(); // Stop observing once we've found the element
    }
  });

  // Start observing
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
}
